import { Service } from '@angular/core';
import { IMAGE_COMPRESSION_SETTINGS } from '@settings/image-compression-settings';

interface ReductionResult {
  dataUrl: string;
  sizeKb: number;
  reachedTarget: boolean;
  width: number;
  height: number;
}

@Service()
export class ImageCompressionService {
  /**
   * Compresses an image data URL so it stays within the configured target size.
   * The image is first resized by reducing its dimensions and, for JPEG images,
   * by lowering the encoding quality when dimension reduction alone is not enough.
   * Returns the original data URL when it is already within the target size.
   */
  async compressDataUrlAsync(dataUrl: string): Promise<string> {
    const contentType = this.extractContentType(dataUrl);
    const currentSizeKb = this.estimateSizeKb(dataUrl);
    if (currentSizeKb <= IMAGE_COMPRESSION_SETTINGS.targetSizeKb) return dataUrl;

    const image = await this.loadImageAsync(dataUrl);
    const dimensionResult = await this.reduceDimensionsAsync(image, contentType, dataUrl, currentSizeKb);
    if (dimensionResult.reachedTarget) return dimensionResult.dataUrl;

    if (dimensionResult.sizeKb > IMAGE_COMPRESSION_SETTINGS.targetSizeKb && contentType === 'image/jpeg') {
      const qualityResult = await this.reduceQualityAsync(image, dimensionResult, contentType);
      return qualityResult.sizeKb < dimensionResult.sizeKb ? qualityResult.dataUrl : dimensionResult.dataUrl;
    }

    return dimensionResult.dataUrl;
  }

  private async reduceDimensionsAsync(
    image: HTMLImageElement,
    contentType: string,
    initialDataUrl: string,
    initialSizeKb: number,
  ): Promise<ReductionResult> {
    const { initialReductionFactor, subsequentReductionFactor, maxIterations, targetSizeKb } = IMAGE_COMPRESSION_SETTINGS;
    let best: ReductionResult = {
      dataUrl: initialDataUrl,
      sizeKb: initialSizeKb,
      reachedTarget: false,
      width: image.width,
      height: image.height,
    };
    let bestBlob: Blob | null = null;
    let reductionFactor = initialReductionFactor;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return best;

    for (let iteration = 0; iteration < maxIterations; iteration++) {
      const width = Math.round(image.width * reductionFactor);
      const height = Math.round(image.height * reductionFactor);
      canvas.width = width;
      canvas.height = height;
      context.drawImage(image, 0, 0, width, height);

      const blob = await this.canvasToBlobAsync(canvas, contentType);
      const sizeKb = blob.size / 1024;
      if (sizeKb < best.sizeKb) {
        bestBlob = blob;
        best = { ...best, sizeKb, width, height };
      }
      if (sizeKb <= targetSizeKb) {
        return { dataUrl: await this.blobToDataUrlAsync(blob), sizeKb, reachedTarget: true, width, height };
      }

      reductionFactor *= subsequentReductionFactor;
      await this.yieldToUiAsync();
    }

    if (bestBlob) best = { ...best, dataUrl: await this.blobToDataUrlAsync(bestBlob) };
    return best;
  }

  private async reduceQualityAsync(
    image: HTMLImageElement,
    dimensionResult: ReductionResult,
    contentType: string,
  ): Promise<ReductionResult> {
    const { jpegQualitySteps, targetSizeKb } = IMAGE_COMPRESSION_SETTINGS;
    let best: ReductionResult = { ...dimensionResult, reachedTarget: false };
    let bestBlob: Blob | null = null;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return best;

    canvas.width = dimensionResult.width;
    canvas.height = dimensionResult.height;
    context.drawImage(image, 0, 0, dimensionResult.width, dimensionResult.height);

    for (const quality of jpegQualitySteps) {
      const blob = await this.canvasToBlobAsync(canvas, contentType, quality);
      const sizeKb = blob.size / 1024;
      if (sizeKb < best.sizeKb) {
        bestBlob = blob;
        best = { ...best, sizeKb };
      }
      if (sizeKb <= targetSizeKb) {
        return { ...best, dataUrl: await this.blobToDataUrlAsync(blob), sizeKb, reachedTarget: true };
      }

      await this.yieldToUiAsync();
    }

    if (bestBlob) best = { ...best, dataUrl: await this.blobToDataUrlAsync(bestBlob) };
    return best;
  }

  private extractContentType(dataUrl: string): string {
    const match = /^data:(image\/[a-z]+);base64,/i.exec(dataUrl);
    return match?.[1]?.toLowerCase() === 'image/png' ? 'image/png' : 'image/jpeg';
  }

  private estimateSizeKb(dataUrl: string): number {
    const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
    let padding = 0;
    if (base64.endsWith('==')) padding = 2;
    else if (base64.endsWith('=')) padding = 1;
    const sizeInBytes = (base64.length * 3) / 4 - padding;
    return sizeInBytes / 1024;
  }

  private loadImageAsync(dataUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Failed to load image for compression'));
      image.src = dataUrl;
    });
  }

  private canvasToBlobAsync(canvas: HTMLCanvasElement, contentType: string, quality?: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Failed to convert canvas to blob'))),
        contentType,
        quality,
      );
    });
  }

  private blobToDataUrlAsync(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error ?? new Error('Failed to read blob'));
      reader.readAsDataURL(blob);
    });
  }

  private yieldToUiAsync(): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, 0));
  }
}
